import { createStore } from "solid-js/store";

import { ListSearch } from "./ListSearch";
import { ListAction } from "./ListAction";
import { ListSorting } from "./ListSorting";
import { ListPagination } from "./ListPagination";
import { ListFilter } from "./ListFilter";
import { ListSummary } from "./ListSummary";
import { createEffect, createSignal, For } from "solid-js";
import { FilterItem } from "./FilterItem";
import { useConfirmer } from "@/context/confirmer";
import { request } from "@/requests/request";
import { applyFilters, applyPagination, applySearch, applySorting } from "./../datasource/local";

export const ListControl = (props) => {

  const { showConfirmer } = useConfirmer();

  const [loading, setLoading] = createSignal(false);
  const [filtered, setFiltered] = createSignal(false);

  const [store, setStore] = createStore({
    data: [],
    checkedItems: [],
    pagination: {
      currentPage: props.pagination.current ?? 1,
      perPage: props.pagination.perPage ?? 10,
      changePerPage: props.pagination.changePerPage ?? true,
      options: props.pagination.options ?? [5, 10, 20, 50, 100],
      totalRows: 0,
      fromRow: 0,
      toRow: 0,
    },
    search: props.search,
    filters: [],
    sorting: {
      column: props.sort.column,
      direction: props.sort.direction
    }
  });

  const onCustomAction = (action, keyOrKeys) => {
    props?.onCustomAction(action, keyOrKeys);
  }

  const onAction = (action, keys) => {
    const callback = async () => {
      await refresh();
      props.onAfterAction?.();
    };
    showConfirmer(action.url, keys, action.title, action.message, callback, action.button, action.confirm, action.text)
  }

  const onBulkAction = (action) => {
    if (action.custom === true) {
      onCustomAction(action.name, store.checkedItems);
      return;
    }

    onAction(action, store.checkedItems);
  }

  const onRowCheck = (key, isChecked) => {
    let checked = store.checkedItems.includes(key);

    if (!checked) {
      setStore("checkedItems", [...store.checkedItems, key]);
      return;
    }

    if (store.checkedItems.length === 1) {
      setStore("checkedItems", []);
      return;
    }

    setStore("checkedItems", store.checkedItems.filter((it) => it !== key));
  }

  const onListCheckChange = (check) => {
    if (!check) {
      setStore('checkedItems', []);
      return;
    }

    setStore('checkedItems', store.data.map((it) => it.key));
  }

  const onRowAction = (action, key, bypass = false) => {
    if (bypass) {
      props.onAction(action, key);
      return;
    }

    onAction(action, [key]);
  }

  const onSearch = (search) => {
    setStore("search", search);
  }

  const onSort = (column, direction) => {
    setStore("sorting", { column, direction });
  }

  const onPageChange = (page) => {
  }

  const onFilter = (filter, operator, value) => {

    if (store.filters.find((f) => f.filter.name === filter.name)) {

      if (filter.type === 'choice' && value.length === 0) {
        onFilterRemove(filter);
        return;
      }

      setStore("filters", store.filters.map((f) => {
        if (f.filter.name === filter.name) {
          return { filter, operator, value };
        }
        return f;
      }));

      return;
    }

    setStore("filters", [...store.filters, { filter, operator, value }]);
  }

  const onFilterUpdate = (filter, operator, value) => {
    setStore("filters", store.filters.map((f) => {
      if (f.filter.name === filter.name) {
        return { filter, operator, value };
      }
      return f;
    }));
  }

  const onFilterRemove = (filter) => {
    setStore("filters", store.filters.filter((f) => f.filter.name !== filter.name));
  }

  const refresh = async () => {
    setLoading(true);
    try {
      if (props.datasource.type === 'local') {
        refreshLocal(props.datasource.data);
      } else if (props.datasource.type === 'remote') {
        refreshRemote(props.datasource.config);
      }
    } catch (error) { }
    finally {
      setFiltered(store.search !== '' || store.filters.length > 0);
      setLoading(false);
    }
  }

  const refreshLocal = (data) => {
    const searchedData = applySearch(data, store.search, props.searchDefinition);
    const filteredData = applyFilters(searchedData, store.filters); 
    const sortedData = applySorting(filteredData, store.sorting);
    const paginatedData = applyPagination(sortedData, store.pagination);

    setStore("data", paginatedData);
    setStore("pagination", {
      ...store.pagination,
      totalRows: sortedData.length,
      fromRow: (store.pagination.currentPage - 1) * store.pagination.perPage + 1,
      toRow: Math.min(store.pagination.currentPage * store.pagination.perPage, sortedData.length),
    });
  }

  const refreshRemote = async (config) => {
    const form = {
      keyword: store.search,
      column: store.sorting.column,
      direction: store.sorting.direction,
      items: store.pagination.perPage,
      page: store.pagination.currentPage,
      filters: store.filters
    };

    const params = config.params;
    const keys = Object.keys(params);

    keys.forEach(key => {
      form[key] = params[key];
    });

    const response = await request(config.url, form);
    if (response.success == false) {
      setStore('data', []);
      errorsToast(response.messages);
      return;
    }

    const list = response.data;
    setStore('data', list);
    setStore('pagination', {
      ...store.pagination,
      currentPage: response.currentPage,
      totalRows: response.total,
      fromRow: response.from,
      toRow: response.to
    });
  }

  createEffect(() => {
    props.data;
    refresh();
  });

  props.ref?.({
    refresh: () => refresh(),
  });

  return (
    <div class="w-full space-y-2.5 mt-2">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <Show when={props.actions !== false && props.actions.length > 0}>
            <ListAction actions={props.actions} items={store.data.length} mode={props.mode} checkedItems={store.checkedItems} onAction={onBulkAction} onCheck={onListCheckChange} />
          </Show>
          <Show when={props.sort !== false && props.sort.options?.length > 0}>
            <ListSorting sorting={store.sorting} options={props.sort.options} onChange={onSort} />
          </Show>
        </div>
        <div class="flex items-center gap-2">
          <Show when={props.search !== false}>
            <ListSearch form={store} onSearch={onSearch} />
          </Show>
          <Show when={props.filters !== false && props.filters.length > 0}>
            <ListFilter filters={props.filters} onFilter={onFilter} />
          </Show>
        </div>
      </div>
      <Show when={store.filters.length > 0}>
        <div class="flex flex-wrap items-center gap-2">
          <For each={store.filters}>{(filter) => (
            <FilterItem filter={filter.filter} value={filter.value} operator={filter.operator} onUpdate={onFilterUpdate} onRemove={() => { onFilterRemove(filter.filter) }} />
          )}</For>
        </div>
      </Show>
      {props.children({ data: store.data, actions: { actions: props.actions, onBulkAction: onBulkAction, onListCheckChange: onListCheckChange }, checkedItems: store.checkedItems, loading: loading(), onRowCheck: onRowCheck, onRowAction: onRowAction, onCustomAction: onCustomAction })}
      <Show when={props.pagination !== false}>
        <div class="flex w-full flex-col-reverse items-center justify-between gap-4 pt-1 pb-2 overflow-auto sm:flex-row">
          <div class="flex items-center gap-2">
            <ListSummary pagination={store.pagination} onChange={(perPage) => { setStore('pagination', { ...store.pagination, perPage }) }} />
          </div>
          <div class="flex flex-col-reverse items-center gap-4 sm:flex-row">
            <ListPagination pagination={store.pagination} onChange={onPageChange} />
          </div>
        </div>
      </Show>
    </div>
  );
}
